import i18n from "../i18n";
import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Button,
  Group,
  Stack,
  TextInput,
  Select,
  NumberInput,
  Text,
  Divider,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { IconX, IconFilter, IconClearAll } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import moment from "moment";
import _ from "lodash";
import { DatePicker } from "@mantine/dates";
import JsonViwer from "./jsonViewer";
import RemoteSelect2 from "./remoteSelect2";

const AdvancedFilterModal = ({
  opened,
  onClose,
  advanceFilters = [],
  onApplyFilters,
  initialValues = {},
}) => {
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState(initialValues);
  const [hasChanges, setHasChanges] = useState(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (opened) {
      // Only initialize form values if we haven't initialized yet
      if (!initializedRef.current) {
        const initializedValues = { ...initialValues };

        // Ensure all dateRange fields have both start and end values initialized
        advanceFilters.forEach((filter) => {
          if (filter.type === "dateRange") {
            const startField = filter.field + ".dateRange1";
            const endField = filter.field + ".dateRange2";

            if (!(startField in initializedValues)) {
              initializedValues[startField] = "";
            }
            if (!(endField in initializedValues)) {
              initializedValues[endField] = "";
            }
          }
        });

        setFormValues(initializedValues);
        initializedRef.current = true;
      } else {
        // Preserve existing form values when reopening
        const currentValues = { ...formValues };

        // Ensure all dateRange fields exist in current values
        advanceFilters.forEach((filter) => {
          if (filter.type === "dateRange") {
            const startField = filter.field + ".dateRange1";
            const endField = filter.field + ".dateRange2";

            if (!(startField in currentValues)) {
              currentValues[startField] = "";
            }
            if (!(endField in currentValues)) {
              currentValues[endField] = "";
            }
          }
        });

        setFormValues(currentValues);
      }
      setHasChanges(false);
    }
  }, [opened, initialValues, advanceFilters]);

  const handleFieldChange = (field, value) => {
    const newValues = { ...formValues };

    // Handle date fields - convert to ISO string for storage
    const filter = advanceFilters.find((f) => f.field === field);
    if (filter?.type === "date" || filter?.type === "dateRange") {
      newValues[field] = value ? moment(value).toISOString() : "";
    } else {
      newValues[field] = value;
    }

    setFormValues(newValues);
    setHasChanges(true);
  };

  const handleClearAll = () => {
    const clearedValues = {};
    advanceFilters.forEach((filter) => {
      if (filter.type === "dateRange") {
        // For dateRange, clear both start and end fields
        const startField = filter.field + ".dateRange1";
        const endField = filter.field + ".dateRange2";
        clearedValues[startField] = "";
        clearedValues[endField] = "";
      } else {
        clearedValues[filter.field] = "";
      }
    });
    setFormValues(clearedValues);
    setHasChanges(true);
  };

  const resetForm = () => {
    initializedRef.current = false;
    setHasChanges(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleApply = () => {
    // Only include non-empty values in the query
    const query = {};
    Object.keys(formValues).forEach((key) => {
      const value = formValues[key];
      if (value !== null && value !== undefined && value !== "") {
        let k = key.replace(".dateRange1", "").replace(".dateRange2", "");
        const filter = advanceFilters.find((f) => f.field === k);

        if (filter) {
          switch (filter.type) {
            case "dateRange":
              // For dateRange, we need to handle both start and end fields
              // start of the date
              if (!query[k]) {
                query[k] = {};
              }
              if (key.includes(".dateRange1")) {
                query[k]["$gte"] = moment(value).startOf("day").toISOString();
              } else if (key.includes(".dateRange2")) {
                query[k]["$lte"] = moment(value).endOf("day").toISOString();
              }
              break;

            case "select":
              query[key] = value;
              break;
            case "text":
              query[key] = { $regex: value, $options: "i" };
              break;
            case "number":
              query[key] = value;
              break;
            default:
              query[key] = value;
          }
        }
      }
    });

    onApplyFilters(query);
    onClose();
  };

  const renderFilterField = (filter) => {
    const value = formValues[filter.field];

    switch (filter.type) {
      case "dateRange":
        // For dateRange, we need to handle both start and end dates
        const startField = filter.field + ".dateRange1";
        const endField = filter.field + ".dateRange2";
        const startValue = formValues[startField];
        const endValue = formValues[endField];

        return (
          <Group spacing="xs" grow>
            <DatePicker
              key={startField}
              label={`${filter.label}`}
              placeholder="From"
              value={startValue ? moment(startValue).toDate() : null}
              onChange={(date) => handleFieldChange(startField, date)}
              clearable
              allowFreeInput
              size="xs"
            />
            <DatePicker
              key={endField}
              label={` `}
              placeholder="To"
              value={endValue ? moment(endValue).toDate() : null}
              onChange={(date) => handleFieldChange(endField, date)}
              clearable
              allowFreeInput
              size="xs"
            />
          </Group>
        );

      case "date":
        // Enhance label for date range fields
        let dateLabel = filter.label;
        if (filter.field.includes(".start")) {
          dateLabel = `${filter.label} (From)`;
        } else if (filter.field.includes(".end")) {
          dateLabel = `${filter.label} (To)`;
        }

        return (
          <DatePicker
            key={filter.field}
            label={dateLabel}
            placeholder={filter.placeholder || "Pick a date"}
            value={value ? moment(value).toDate() : null}
            onChange={(date) => handleFieldChange(filter.field, date)}
            clearable
            allowFreeInput
            size="xs"
          />
        );

      case "RemoteSelect":
        return (
          <RemoteSelect2
            key={filter.field}
            label={filter.label}
            {...filter.props}
            value={value || ""}
            onDataChange={(data) => {
              if (!data) {
                handleFieldChange(filter.field, null);
                return;
              }
              if (data && data[filter.props?.valueField ?? "_id"]) {
                handleFieldChange(
                  filter.field,
                  data[filter.props?.valueField ?? "_id"]
                );
              }
            }}
            clearable
            allowFreeInput
            size="xs"
          />
        );

      case "select":
        return (
          <Select
            key={filter.field}
            label={filter.label}
            placeholder={filter.placeholder || "Select an option"}
            data={filter.options || []}
            value={value}
            onChange={(val) => handleFieldChange(filter.field, val)}
            clearable
            size="xs"
          />
        );

      case "text":
        return (
          <TextInput
            key={filter.field}
            label={filter.label}
            placeholder={filter.placeholder || "Enter text"}
            value={value || ""}
            onChange={(e) =>
              handleFieldChange(filter.field, e.currentTarget.value)
            }
            size="xs"
          />
        );

      case "number":
        return (
          <NumberInput
            key={filter.field}
            label={filter.label}
            placeholder={filter.placeholder || "Enter number"}
            value={value}
            onChange={(val) => handleFieldChange(filter.field, val)}
            size="xs"
          />
        );

      default:
        return (
          <TextInput
            key={filter.field}
            label={filter.label}
            placeholder={filter.placeholder || "Enter value"}
            value={value || ""}
            onChange={(e) =>
              handleFieldChange(filter.field, e.currentTarget.value)
            }
            size="xs"
          />
        );
    }
  };

  const hasActiveFilters = Object.values(formValues).some((value) => {
    // Check for all falsy values that should be considered "empty"
    if (value === null || value === undefined || value === "") return false;
    if (Array.isArray(value) && value.length === 0) return false;
    if (typeof value === "object" && Object.keys(value).length === 0)
      return false;
    return true;
  });

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group spacing="xs">
          <IconFilter size={16} />
          <Text>{i18n.t("Advanced Filters")}</Text>
          {hasActiveFilters && (
            <Text size="xs" color="blue" weight={500}>
              (
              {
                Object.values(formValues).filter((v) => {
                  if (v === null || v === undefined || v === "") return false;
                  if (Array.isArray(v) && v.length === 0) return false;
                  if (typeof v === "object" && Object.keys(v).length === 0)
                    return false;
                  return true;
                }).length
              }{" "}
              active)
            </Text>
          )}
        </Group>
      }
      size="xl"
      closeOnClickOutside={false}
    >
      <Stack spacing="md">
        {advanceFilters.length === 0 ? (
          <Text color="dimmed" align="center" py="xl">
            No advanced filters configured
          </Text>
        ) : (
          <>
            {advanceFilters.map((filter) => (
              <div key={filter.field}>{renderFilterField(filter)}</div>
            ))}

            <Divider />

            <Group position="apart">
              <Tooltip label="Clear all filters">
                <Button
                  color="orange"
                  size="xs"
                  onClick={handleClearAll}
                  disabled={!hasActiveFilters}
                >{i18n.t("Clear")}</Button>
              </Tooltip>

              <Group spacing="xs">
                <Button variant="default" size="xs" onClick={handleClose}>{i18n.t("Cancel")}</Button>
                <Button
                  size="xs"
                  onClick={handleApply}
                  disabled={_.isEmpty(formValues)}
                >
                  Apply Filters
                </Button>
              </Group>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
};

export default AdvancedFilterModal;
